/**
 * UpdateActionEditForm — click-to-edit, auto-saving diff for UpdateActionCard.
 *
 * Renders the same DiffSection the read-only card renders — canonical CRM
 * value labels on both sides — and swaps ONE proposed cell at a time for the
 * matching `PropertyInput` when the user clicks it. This mirrors the CRM
 * table's cell interaction; 18 permanently-live inputs on an enrichment
 * proposal read as a wall of form controls and invite accidental edits.
 *
 * The "current" column is existing CRM state and is never editable.
 *
 * There is no Save button. Editing a value writes into the pending payload
 * draft and arms a debounced save; committing the editor (Enter, blur, or
 * picking from a popover) flushes that save immediately. The save is
 * registered with `AutoSaveCoordination`, so an edit made moments before
 * Approve is flushed by the footer's `flushAllAndWait()` instead of being
 * overtaken by the approval — and it is suppressed once approval freezes
 * autosave, because the backend rejects `update_payload` from then on.
 *
 * For stage/lifecycle properties, renders `AssociatedPipelineStageInput`
 * — the same stage selector used in CRM record editing.
 */

import { useCallback, useMemo, useRef } from 'react';

import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { usePipelineStages } from '@/lib/crm/hooks/usePipelineStages';
import { useCrmEntityProps } from '@/lib/crm/object-cards/useCrmEntityProps';
import { ENTITY_TYPE_TO_STAGE_ASSOC } from '@/lib/proposals/stageAssociationTypes';

import type { StageAssociationValue } from '../../lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { usePropertySchema } from '../../lib/crm/hooks/usePropertySchema';
import type {
  PropertyUpdateEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { wrapProperties } from './CreateActionCard.helpers';
import { DiffSection } from './DiffSection';
import {
  type CoordinatedSaveStatus,
  useActionPayloadSave,
  useCoordinatedAutoSave,
} from './shared';
import { buildEditRows } from './UpdateActionEditForm.editRows';
import { isStageProperty } from './UpdateActionEditForm.helpers';
import { useCurrentStageValue } from './useCurrentStageValue';
import { useInlineEditTarget } from './useInlineEditTarget';
import { useStageResolvedEditValues } from './useStageResolvedEditValues';

/** Human-readable autosave status (null = render nothing). */
const SAVE_STATUS_LABEL: Partial<Record<CoordinatedSaveStatus, string>> = {
  saving: 'Saving…',
  saved: 'Saved',
  error: "Couldn't save – keep editing to retry",
};

// ─── Component ───────────────────────────────────────────────────────────────

interface UpdateActionEditFormProps {
  action: ProposedAction<PropertyUpdateEntityPayload>;
  enrichedData: ActionRendererProps['enrichedData'];
  proposalId?: number;
  orgId?: number;
  onUpdate?: ActionRendererProps['onUpdate'];
  /**
   * Property to open for editing on mount — the value the user clicked in the
   * read-only card, which is what mounted this form.
   */
  initialEditKey?: string | null;
}

export function UpdateActionEditForm({
  action,
  enrichedData,
  proposalId,
  orgId,
  onUpdate,
  initialEditKey = null,
}: UpdateActionEditFormProps) {
  // Autosave reports progress inline, so the per-save toast would be noise.
  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
    showSuccessToast: false,
  });

  const { payload } = action.payload;
  const entityTypeName = action.payload.entity_type_name;
  const entityId = action.payload.entity_id;
  const {
    properties: proposedProperties,
    original_values: originalValues = {},
  } = payload;

  // CRM store data for current value fallback
  const crmEntityId = entityId ? entityId : undefined;
  const crmFlatProps = useCrmEntityProps(orgId ?? 0, crmEntityId);

  // Fetch property schemas so we can render the correct label and input for
  // each field. Shares one React Query entry with the read-only card.
  const { schema } = usePropertySchema(orgId ?? '', entityTypeName);

  // Dynamic stage association type lookup from schema API.
  // Falls back to hardcoded mapping when schema doesn't return
  // target_entity_kind_name (current backend behavior).
  const { stageAssocForEntityType } = useAssociationTypes(orgId);
  const stageAssociationType =
    stageAssocForEntityType.get(entityTypeName) ??
    ENTITY_TYPE_TO_STAGE_ASSOC[entityTypeName];

  // Fetch all stages for resolving stage names → entity_ids.
  const { stages } = usePipelineStages(orgId, entityTypeName);

  const propertyKeys = useMemo(
    () => Object.keys(proposedProperties),
    [proposedProperties]
  );

  const stagePropertyKeys = useMemo(
    () => propertyKeys.filter(isStageProperty),
    [propertyKeys]
  );

  // Look up the entity's current stage from CRM store associations.
  const currentStageValues = useCurrentStageValue(
    orgId,
    crmEntityId,
    stagePropertyKeys,
    entityTypeName
  );

  const { editValues, setEditValues } = useStageResolvedEditValues({
    propertyKeys,
    proposedProperties,
    stages,
  });

  // Latest-ref so a change handler can build the next draft synchronously —
  // two edits in the same tick must both land, and the handler must not take
  // editValues as a dependency (that would rebuild every diff row per
  // keystroke and retrigger AssociatedPipelineStageInput's auto-select).
  const editValuesRef = useRef(editValues);
  editValuesRef.current = editValues;

  const buildPayload = useCallback(
    (values: Record<string, unknown>): PropertyUpdateEntityPayload => ({
      ...action.payload,
      payload: {
        ...payload,
        properties: wrapProperties(values),
      },
    }),
    [action.payload, payload]
  );
  const buildPayloadRef = useRef(buildPayload);
  buildPayloadRef.current = buildPayload;

  const persist = useCallback(
    (updatedPayload: PropertyUpdateEntityPayload) =>
      savePayload(updatedPayload),
    [savePayload]
  );

  const { save, flush, saveStatus } = useCoordinatedAutoSave({
    registrationId: `property-update-${action.id}`,
    onSave: persist,
    // What the server already holds — reverting to it must not round-trip.
    initialSavedValue: action.payload,
    enabled: !!orgId && !!proposalId,
  });

  /** Write into the draft and arm the debounced save. */
  const handleChange = useCallback(
    (key: string, value: unknown) => {
      const next = { ...editValuesRef.current, [key]: value };
      editValuesRef.current = next;
      setEditValues(next);
      save(buildPayloadRef.current(next));
    },
    [save, setEditValues]
  );

  const handleStageChange = useCallback(
    (key: string, assoc: StageAssociationValue | null) => {
      handleChange(key, assoc?.target_entity_id ?? null);
    },
    [handleChange]
  );

  const { activeEditKey, openEditor, commitEditor, cancelEditor } =
    useInlineEditTarget({ editValues, setEditValues, initialEditKey });

  /** Enter / blur / popover selection: close the editor and persist now. */
  const handleCommit = useCallback(() => {
    commitEditor();
    void flush();
  }, [commitEditor, flush]);

  /**
   * Escape: restore the value the field had when it opened, then re-arm with
   * the restored draft so an already-armed save cannot write the text the user
   * just discarded (an unchanged payload is dropped rather than round-tripped).
   */
  const handleCancelEdit = useCallback(() => {
    const restored = cancelEditor();
    if (!restored) {
      return;
    }
    editValuesRef.current = restored;
    save(buildPayloadRef.current(restored));
  }, [cancelEditor, save]);

  // Build diff rows: read-only canonical labels, with the active row swapped
  // for its input.
  const currentValues = enrichedData?.current_values;
  const editRows = useMemo(
    () =>
      buildEditRows({
        propertyKeys,
        originalValues,
        currentValues,
        crmFlatProps,
        currentStageValues,
        schema,
        orgId,
        activeEditKey,
        stageAssociationType,
        entityTypeName,
        editValues,
        handleChange,
        handleStageChange,
        openEditor,
        handleCommit,
        handleCancelEdit,
      }),
    [
      propertyKeys,
      originalValues,
      currentValues,
      crmFlatProps,
      currentStageValues,
      schema,
      orgId,
      activeEditKey,
      stageAssociationType,
      entityTypeName,
      editValues,
      handleChange,
      handleStageChange,
      openEditor,
      handleCommit,
      handleCancelEdit,
    ]
  );

  const saveStatusText = SAVE_STATUS_LABEL[saveStatus] ?? null;

  return (
    <div className='space-y-1.5'>
      {/* a11y: announce save-status changes (sr-only is absolute — no layout impact). */}
      <div aria-atomic='true' aria-live='polite' className='sr-only'>
        {saveStatusText ?? ''}
      </div>
      {saveStatusText && (
        <p
          aria-hidden='true'
          className={
            saveStatus === 'error'
              ? 'px-1 text-right text-xs text-utility-error-600'
              : 'px-1 text-right text-xs text-fg-tertiary'
          }
        >
          {saveStatusText}
        </p>
      )}
      <DiffSection rows={editRows} />
    </div>
  );
}
